import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Menu, MenuItem, ListItemIcon } from '@mui/material';
import { styled } from '@mui/system';
import {
  NewBuildingModel,
  UserModel,
  AdFieldStatusEnum,
  NotificationTypeEnum,
  UserKindEnum,
} from '@/types';
import { offPlanProvider } from '@/providers';
import { appSetNotification } from '@/store';
import { icons } from '../Ad/Icons/icons';
import { Loader } from '@/components';

const StyledMenuItem = styled(MenuItem)({
  fontWeight: '400',
});

export const OffPlanViewMenu = ({
  page,
  open,
  handleClose,
  anchorEl,
  user,
  setIsShownConfirmDialog,
}: {
  page: NewBuildingModel;
  open: boolean;
  handleClose: () => void;
  anchorEl: Element | null;
  user: UserModel;
  setIsShownConfirmDialog: (value: boolean) => void;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [statusAd, setStatusAd] = useState<AdFieldStatusEnum>(page.status);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isOwner = page.developerId === user.id || user.kind === UserKindEnum.Admin;

  const handleClickEdit = () => {
    navigate(`/admin/offplan/${page.id}`);
  };

  const handlerClickArchived = async (fieldStatus: AdFieldStatusEnum) => {
    setIsLoading(true);
    const { ok, status, message } = await offPlanProvider.updateById(page.id!, {
      status: fieldStatus,
    });
    if (!ok) {
      dispatch(appSetNotification(NotificationTypeEnum.Error, message, status));
    } else {
      setStatusAd(AdFieldStatusEnum.Archived);
    }
    setIsLoading(false);
  };

  const handleCopyLink = async () => {
    const fullUrl = window.location.href;
    try {
      await navigator.clipboard.writeText(fullUrl);
      console.log('URL copied to clipboard');
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        id='account-menu'
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {isOwner && (
          <StyledMenuItem onClick={() => handleClickEdit()}>
            <ListItemIcon>
              <icons.EditComment />
            </ListItemIcon>
            Edit
          </StyledMenuItem>
        )}
        {isOwner && statusAd !== AdFieldStatusEnum.Archived && (
          <StyledMenuItem onClick={() => handlerClickArchived(AdFieldStatusEnum.Archived)}>
            <ListItemIcon>
              <icons.Archive />
            </ListItemIcon>
            Archive
          </StyledMenuItem>
        )}
        {isOwner && (
          <StyledMenuItem onClick={() => navigate(`/admin/plan/create/${page.id}`)}>
            <ListItemIcon>
              <icons.AddPlan />
            </ListItemIcon>
            Add plan
          </StyledMenuItem>
        )}
        {isOwner && (
          <StyledMenuItem onClick={() => setIsShownConfirmDialog(true)}>
            <ListItemIcon>
              <icons.AddBuilding />
            </ListItemIcon>
            Add building
          </StyledMenuItem>
        )}
        {isOwner && (
          <StyledMenuItem onClick={() => navigate(`/admin/units/create/${page.id}`)}>
            <ListItemIcon>
              <icons.Upload />
            </ListItemIcon>
            Upload units
          </StyledMenuItem>
        )}
        <StyledMenuItem onClick={() => navigate(`/offplan/create-pdf/${page.id}`)}>
          <ListItemIcon>
            <icons.Pdf />
          </ListItemIcon>
          Save As PDF
        </StyledMenuItem>
        <StyledMenuItem onClick={() => handleCopyLink()}>
          <ListItemIcon>
            <icons.Link />
          </ListItemIcon>
          Create link
        </StyledMenuItem>
        <StyledMenuItem onClick={() => console.log('Favorite')}>
          <ListItemIcon>
            <icons.Unlike />
          </ListItemIcon>
          Favorite
        </StyledMenuItem>
      </Menu>
    </>
  );
};
